// header-explore.js
import Popup from '../components/Popup.js';
import Alert from '../components/alerts.js';
import { getText, getCurrentLanguage } from '../translations/translations.js';
import { DATA_PATHS } from '../dataPaths.js';
import { displaySpots } from '../map/map.js';
import { clearAllSpots } from '../map/map-visibility.js';
import { IMAGE_PATHS, DOMAIN_NAME, LOGGING, EXPLORE_TAG_SETTINGS } from '/settings.js';
import { getSpotsByExplore, getAreasList, getPacksForArea, getTagsForAreaPack } from '../utils/spot-queries.js';
import { preloadMinimalSpotsForArea, getCachedMinimalSpotsForArea, filterCachedMinimalSpots, convertMinimalToSpots } from '../utils/cache/index.js';
import { triggerAreaBatch } from '../utils/cache/cards-cache.js';
import { setCardsContext } from '../cards/index.js';
import { clearAllUI, setUIState } from '../utils/clearAll.js';
import { setHeaderButtonActive } from './header-buttons.js';
import { updateUrlForExplore, parseUrlForExploreAreaPackTag, normalizeExploreTagsForBackend, parseUrlParams } from '../utils/url-utils.js';
import { trackExplore } from '../analytics/gtm.js';
import { updateMapView } from '../map/map-transitions.js';
import { createIconMarkup, createIconElement } from '../utils/icon.js';
import { sanitizeInput, isForbiddenChar, sanitizePaste } from '../utils/sanitize-utils.js';

/**
 * SEARCH-BASED EXPLORE SYSTEM (2026)
 * Shows all tags in a searchable list with category hashtags
 * Remembers user's last search, filter view, and scroll position
 */
export async function initializeExploreSearch(options = {}) {
    const { preSelectedPack = null } = options;

    // Check if we can reuse an existing popup
    const reusePopup = window.currentPopupInstance && window.currentPopupInstance.element && document.body.contains(window.currentPopupInstance.element);

    // Clear existing UI with animation (but keep popup if reusing, and keep spot selection)
    clearAllUI({ animate: true, keepPopup: reusePopup, keepSpot: true });

    // Current Area is now MANDATORY
    const { areaSlug } = parseUrlParams();
    if (!areaSlug) {
        if (LOGGING.ENABLED && LOGGING.MENU) console.error('Explore initialized without an active area.');
        return;
    }

    // Initialize State
    let selectedArea = null;
    let selectedTags = [];
    let popupInstance = null;
    let allTagsWithPacks = []; // Store all tags with their pack info
    let currentViewMode = 'category'; // 'category' or 'alphabetical'
    let viewModeBeforeSearch = null; // Track mode before auto-switching for search
    let searchQuery = '';
    let isFirstRender = true; // Track if this is the initial render

    // Session storage key for this explore state
    const sessionKey = `exploreState_${areaSlug}`;

    // Load previous session state if it exists
    const previousSessionState = JSON.parse(sessionStorage.getItem(sessionKey)) || {};
    currentViewMode = previousSessionState.viewMode || 'category';
    searchQuery = previousSessionState.searchQuery || '';
    let previousSelectedTagIds = previousSessionState.selectedTagIds || []; // Restore selected tags for visual display

    // Support remembering the last Explore query
    let preloadedTagSlugs = [];
    const previousExploreParams = await parseUrlForExploreAreaPackTag();
    const maxTags = EXPLORE_TAG_SETTINGS.MAX_TAGS_PER_EXPLORE;
    const isSingleTagMode = maxTags === 1;

    // Load tags from URL to pre-select them
    if (previousExploreParams && previousExploreParams.area === areaSlug) {
        if (previousExploreParams.tags) {
            const limitedTags = previousExploreParams.tags.slice(0, maxTags);
            preloadedTagSlugs = normalizeExploreTagsForBackend(limitedTags || []);
        }
    }

    // Setup Popup (Reuse or Create)
    const loadingContent = '<div style="text-align: center; padding: 40px;">Loading explore...</div>';

    if (reusePopup) {
        popupInstance = window.currentPopupInstance;
        popupInstance.updateContent({
            title: 'Loading...',
            content: loadingContent,
            footer: ''
        });
        popupInstance.updateOnClose(() => {
            popupInstance = null;
            window.currentPopupInstance = null;
            setUIState('popupOpen', false);
            window.dispatchEvent(new CustomEvent('explorePopupClosed'));
        });
        popupInstance.element.classList.remove('fading-out');
        popupInstance.element.classList.remove('fade-out');
    } else {
        popupInstance = Popup({
            title: 'Loading...',
            content: loadingContent,
            footer: '',
            size: 'full',
            onClose: () => {
                popupInstance = null;
                window.currentPopupInstance = null;
                setUIState('popupOpen', false);
                window.dispatchEvent(new CustomEvent('explorePopupClosed'));
            }
        });
        window.currentPopupInstance = popupInstance;

        if (popupInstance && popupInstance.element) {
            popupInstance.element.classList.add('fade-in');
        }
        setUIState('popupOpen', true);
    }

    // Load Area Data & Start Flow
    try {
        const areas = await getAreasList();
        selectedArea = areas.find(a => a.slug === areaSlug);

        if (!selectedArea) {
            if (LOGGING.ENABLED && LOGGING.MENU) console.error(`Area not found for slug: ${areaSlug}`);
            popupInstance.close();
            return;
        }

        // Show the search-based view
        await showSearchView();

    } catch (err) {
        if (LOGGING.ENABLED && LOGGING.MENU) console.error('Error initializing explore search:', err);
        popupInstance.close();
    }

    // Main search view function
    async function showSearchView() {
        setHeaderButtonActive('explore');

        try {
            // **NEW: Preload ALL minimal spots for this area (background, non-blocking)**
            preloadMinimalSpotsForArea(selectedArea.id).catch(err => {
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.warn('[Explore] Failed to preload area minimal spots:', err);
                }
            });

            // Load all packs and their tags
            const packs = await getPacksForArea(selectedArea.id);
            const lang = getCurrentLanguage();
            const exploreTitle = await getText('buttons.exploreTitle');
            const areaName = selectedArea[`name_${lang}`] || selectedArea.name_en;
            const title = `${exploreTitle} ${areaName}`;

            // Preload tags for all packs in background (batch) and limit card preload concurrency
            if (packs && packs.length > 0) {
                const packIds = packs.map(p => p.id).filter(Boolean);
                // trigger tag preloads (background helper)
                const cacheModule = await import('../utils/cache/index.js');
                if (cacheModule.preloadTagsForPacks) {
                    try {
                        cacheModule.preloadTagsForPacks(selectedArea.id, packIds);
                    } catch (e) {
                        // ignore background preload errors
                    }
                }

                // BATCH OPTIMIZATION: One call for all packs in the area
                // Start preloading cards in background (non-blocking)
                if (packIds.length > 0) {
                    triggerAreaBatch(selectedArea.id);
                }

                // Try to fetch tags for all packs in a single batch call (fast path)
                let tagsByPack = {};
                try {
                    const { batchLoadTagsForPacks } = await import('../utils/cache/index.js');
                    if (batchLoadTagsForPacks) {
                        const packIdMap = await batchLoadTagsForPacks(selectedArea.id, packIds);
                        // Convert Map to plain object for the existing logic
                        tagsByPack = Object.fromEntries(packIdMap);
                    }
                } catch (e) {
                    // fallback to per-pack fetch below
                    if (LOGGING.ENABLED && LOGGING.CACHE) console.warn('[Explore] batch tags fetch failed, falling back to per-pack fetch', e);
                    tagsByPack = {};
                }

                // Build tag list from batch result or fallback to individual calls
                allTagsWithPacks = [];
                if (tagsByPack && Object.keys(tagsByPack).length > 0) {
                    for (const pack of packs) {
                        const tags = tagsByPack[pack.id] || tagsByPack[String(pack.id)] || [];
                        tags.forEach(tag => {
                            allTagsWithPacks.push({
                                ...tag,
                                packId: pack.id,
                                packSlug: pack.slug,
                                packName: pack[`name_${lang}`] || pack.name_en,
                                packIcon: pack.icon
                            });
                        });
                    }
                } else {
                    for (const pack of packs) {
                        const tags = await getTagsForAreaPack(selectedArea.id, pack.id);
                        tags.forEach(tag => {
                            allTagsWithPacks.push({
                                ...tag,
                                packId: pack.id,
                                packSlug: pack.slug,
                                packName: pack[`name_${lang}`] || pack.name_en,
                                packIcon: pack.icon
                            });
                        });
                    }
                }

                // Populate initial selectedTags array from URL/Session IDs ONCE
                selectedTags = [];
                allTagsWithPacks.forEach(tag => {
                    const isInPreloaded = preloadedTagSlugs.includes(tag.slug);
                    const isInPrevious = previousSelectedTagIds.includes(tag.id);
                    if (isInPreloaded || isInPrevious) {
                        if (selectedTags.length < maxTags) {
                            const alreadyAdded = selectedTags.find(t => t.id === tag.id);
                            if (!alreadyAdded) {
                                selectedTags.push(tag);
                            }
                        }
                    }
                });
            }

            // Create main container
            const content = document.createElement('div');
            content.className = 'explore-search-container';

            // Create search header
            const searchHeader = document.createElement('div');
            searchHeader.className = 'explore-search-header';

            // Search input
            const searchInputContainer = document.createElement('div');
            searchInputContainer.className = 'explore-search-input-container';

            const searchInput = document.createElement('input');
            searchInput.type = 'text';
            searchInput.className = 'explore-search-input';
            searchInput.placeholder = await getText('search.placeholder') || 'Search tags...';
            searchInput.value = searchQuery; // Restore previous search query
            searchInput.name = 'explore-search';
            searchInput.autocomplete = 'off';
            searchInput.autocapitalize = 'off';
            searchInput.autocorrect = 'off';
            searchInput.spellcheck = false;
            searchInput.inputMode = 'search';

            // Create search and clear icons
            const searchIcon = createIconElement('magnifying-glass-solid', 'explore-search-icon');
            searchIcon.style.display = searchQuery ? 'none' : 'block'; // Show search icon only when empty

            const clearIcon = createIconElement('circle-xmark-regular', 'explore-search-icon explore-clear-icon');
            clearIcon.style.display = searchQuery ? 'block' : 'none'; // Show clear icon only when has text
            clearIcon.style.cursor = 'pointer';

            // Clear button handler
            clearIcon.addEventListener('click', () => {
                searchInput.value = '';
                searchQuery = '';
                searchIcon.style.display = 'block';
                clearIcon.style.display = 'none';
                saveSessionState();

                // Revert to previous view mode if it was automatically changed
                if (viewModeBeforeSearch) {
                    currentViewMode = viewModeBeforeSearch;
                    viewModeBeforeSearch = null;
                    if (currentViewMode === 'category') {
                        categoryBtn.classList.add('active');
                        alphabeticalBtn.classList.remove('active');
                    } else {
                        alphabeticalBtn.classList.add('active');
                        categoryBtn.classList.remove('active');
                    }
                }

                renderTagsList();
                searchInput.focus();
            });

            searchInput.addEventListener('input', (e) => {
                const sanitized = sanitizeInput(e.target.value);
                if (sanitized !== e.target.value) {
                    e.target.value = sanitized;
                }
                searchQuery = sanitized.toLowerCase();
                // Toggle between search and clear icons
                searchIcon.style.display = searchQuery ? 'none' : 'block';
                clearIcon.style.display = searchQuery ? 'block' : 'none';

                saveSessionState();
                renderTagsList();
            });

            searchInput.addEventListener('keydown', (e) => {
                if (isForbiddenChar(e.key)) {
                    e.preventDefault();
                }
            });

            searchInput.addEventListener('paste', (e) => {
                const paste = e.clipboardData?.getData('text') || '';
                const sanitized = sanitizePaste(paste);
                if (sanitized !== paste) {
                    e.preventDefault();
                    const current = searchInput.value;
                    const start = searchInput.selectionStart ?? current.length;
                    const end = searchInput.selectionEnd ?? current.length;
                    searchInput.value = current.slice(0, start) + sanitized + current.slice(end);
                    searchQuery = searchInput.value.toLowerCase();
                    searchIcon.style.display = searchQuery ? 'none' : 'block';
                    clearIcon.style.display = searchQuery ? 'block' : 'none';
                    saveSessionState();
                    renderTagsList();
                }
            });

            searchInputContainer.appendChild(searchInput);
            searchInputContainer.appendChild(searchIcon);
            searchInputContainer.appendChild(clearIcon);

            // View toggle buttons
            const viewToggle = document.createElement('div');
            viewToggle.className = 'explore-view-toggle';


            const byCategoryText = await getText('explore.viewByCategory');
            const alphabeticalText = await getText('explore.viewAlphabetical');

            const categoryBtn = document.createElement('button');
            categoryBtn.className = 'explore-view-toggle-btn' + (currentViewMode === 'category' ? ' active' : '');
            const categoryIcon = createIconElement('categories', 'explore-view-toggle-icon');
            const categoryText = document.createElement('span');
            categoryText.className = 'explore-view-toggle-text';
            categoryText.textContent = byCategoryText;
            categoryBtn.appendChild(categoryIcon);
            categoryBtn.appendChild(categoryText);
            categoryBtn.addEventListener('click', () => {
                if (currentViewMode !== 'category') { // Only if changing view mode
                    currentViewMode = 'category';
                    viewModeBeforeSearch = null; // Clear auto-switch state if user manually toggles
                    categoryBtn.classList.add('active');
                    alphabeticalBtn.classList.remove('active');
                    // Reset scroll position when switching view modes
                    const tagsListScroll = popupInstance?.element?.querySelector('.explore-tags-list');
                    if (tagsListScroll) {
                        tagsListScroll.scrollTop = 0;
                    }
                    saveSessionState();
                    renderTagsList();
                }
            });

            const alphabeticalBtn = document.createElement('button');
            alphabeticalBtn.className = 'explore-view-toggle-btn' + (currentViewMode === 'alphabetical' ? ' active' : '');
            const alphabeticalIcon = createIconElement('arrow-down-a-z-solid-full', 'explore-view-toggle-icon');
            const alphabeticalBtnText = document.createElement('span');
            alphabeticalBtnText.className = 'explore-view-toggle-text';
            alphabeticalBtnText.textContent = alphabeticalText;
            alphabeticalBtn.appendChild(alphabeticalIcon);
            alphabeticalBtn.appendChild(alphabeticalBtnText);
            alphabeticalBtn.addEventListener('click', () => {
                if (currentViewMode !== 'alphabetical') { // Only if changing view mode
                    currentViewMode = 'alphabetical';
                    viewModeBeforeSearch = null; // Clear auto-switch state if user manually toggles
                    alphabeticalBtn.classList.add('active');
                    categoryBtn.classList.remove('active');
                    // Reset scroll position when switching view modes
                    const tagsListScroll = popupInstance?.element?.querySelector('.explore-tags-list');
                    if (tagsListScroll) {
                        tagsListScroll.scrollTop = 0;
                    }
                    saveSessionState();
                    renderTagsList();
                }
            });

            viewToggle.appendChild(categoryBtn);
            viewToggle.appendChild(alphabeticalBtn);

            searchHeader.appendChild(searchInputContainer);
            searchHeader.appendChild(viewToggle);

            // Tags list container
            const tagsListContainer = document.createElement('div');
            tagsListContainer.className = 'explore-tags-list';

            content.appendChild(searchHeader);
            content.appendChild(tagsListContainer);

            // Add "See Spots" button if multi-tag mode
            if (!isSingleTagMode) {
                const actionBtn = document.createElement('button');
                actionBtn.className = 'explore-search-action';
                actionBtn.disabled = true;
                actionBtn.innerHTML = `${createIconMarkup('magnifying-glass-solid', 'icon')}${await getText('buttons.exploreResults')}`;
                actionBtn.addEventListener('click', async () => {
                    await executeExploreAction();
                });
                content.appendChild(actionBtn);
            }

            popupInstance.updateContent({ title, content });

            // Initial render
            renderTagsList();

            // Setup scroll event listener for saving scroll position
            setupScrollSaving();

        } catch (error) {
            if (LOGGING.ENABLED && LOGGING.MENU) console.error('Error showing search view:', error);
        }
    }

    // Render tags list based on current view mode and search query
    async function renderTagsList() {
        if (!popupInstance || !popupInstance.element) return;

        const container = popupInstance.element.querySelector('.explore-tags-list');
        if (!container) return;

        // Toggle layout mode: inline wrapping in category view only
        container.classList.toggle('category-inline', currentViewMode === 'category');

        const lang = getCurrentLanguage();
        container.innerHTML = '';

        // Filter tags by search query
        let filteredTags = allTagsWithPacks.filter(tag => {
            if (searchQuery.length < 2) return true; // Only start showing filtered results after 2 characters
            const tagName = (tag[`name_${lang}`] || tag.name_en || '').toLowerCase();
            const packName = (tag.packName || '').toLowerCase();
            return tagName.includes(searchQuery) || packName.includes(searchQuery);
        });

        if (filteredTags.length === 0) {
            const noResultsText = await getText('search.noResults') || 'No tags found';
            container.innerHTML = `<div class="explore-no-results">${noResultsText}</div>`;
            return;
        }

        // Sort based on view mode
        if (currentViewMode === 'alphabetical') {
            filteredTags.sort((a, b) => {
                const nameA = (a[`name_${lang}`] || a.name_en || '').toLowerCase();
                const nameB = (b[`name_${lang}`] || b.name_en || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
        }

        // Render tags
        if (currentViewMode === 'category') {
            // Group by pack and show separators
            let currentPackId = null;
            let currentTagsContainer = null;
            let isFirstPack = true;

            // Determine which packs have selected tags to expand them by default
            const packIdsWithSelectedTags = new Set(
                allTagsWithPacks
                    .filter(t => preloadedTagSlugs.includes(t.slug) || previousSelectedTagIds.includes(t.id))
                    .map(t => t.packId)
            );

            filteredTags.forEach(tag => {
                // Add pack separator and container if new pack
                if (tag.packId !== currentPackId) {
                    const packGroup = document.createElement('div');
                    packGroup.className = 'explore-pack-group';

                    const separator = document.createElement('div');
                    separator.className = 'explore-pack-separator';
                    const iconId = normalizeIconId(tag.packIcon) || 'map-location-dot-solid';
                    const chevronIcon = createIconMarkup('chevron-down-solid', 'chevron-icon');
                    separator.innerHTML = `${createIconMarkup(iconId, 'icon')} ${tag.packName} ${chevronIcon}`;

                    currentTagsContainer = document.createElement('div');
                    currentTagsContainer.className = 'explore-pack-tags';

                    // Collapsible logic: expand if searching (>=2 chars), if tag is selected, or if it's the first pack
                    const shouldExpand = (searchQuery.length >= 2) || packIdsWithSelectedTags.has(tag.packId) || (packIdsWithSelectedTags.size === 0 && isFirstPack);
                    if (shouldExpand) {
                        packGroup.classList.add('is-expanded');
                    } else {
                        currentTagsContainer.classList.add('is-collapsed');
                    }

                    // Click handler for accordion
                    separator.addEventListener('click', (e) => {
                        e.stopPropagation();
                        const isExpanded = packGroup.classList.contains('is-expanded');

                        // Close all groups
                        const allGroups = container.querySelectorAll('.explore-pack-group');
                        allGroups.forEach(group => {
                            group.classList.remove('is-expanded');
                            const tags = group.querySelector('.explore-pack-tags');
                            if (tags) {
                                tags.classList.add('is-collapsed');
                            }
                        });

                        // If it was closed, open it
                        if (!isExpanded) {
                            packGroup.classList.add('is-expanded');
                            const packTags = packGroup.querySelector('.explore-pack-tags');
                            if (packTags) {
                                packTags.classList.remove('is-collapsed');
                            }

                            // Anchor this category to the top so it doesn't jump
                            // when categories above it collapse.
                            setTimeout(() => {
                                packGroup.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start'
                                });
                            }, 50);
                        }
                    });

                    packGroup.appendChild(separator);
                    packGroup.appendChild(currentTagsContainer);
                    container.appendChild(packGroup);

                    currentPackId = tag.packId;
                    isFirstPack = false;
                }
                currentTagsContainer.appendChild(createTagElement(tag));
            });
        } else {
            // Alphabetical view - just list tags in a flat container
            const alphabeticalContainer = document.createElement('div');
            alphabeticalContainer.className = 'explore-alphabetical-container';
            filteredTags.forEach(tag => {
                alphabeticalContainer.appendChild(createTagElement(tag));
            });
            container.appendChild(alphabeticalContainer);
        }

        updateActionButtonState();

        // Restore scroll position after rendering (only on first render)
        if (isFirstRender) {
            const previousScrollPosition = previousSessionState.scrollPosition || 0;
            if (container.parentElement && previousScrollPosition > 0) {
                setTimeout(() => {
                    const tagsListScroll = popupInstance.element?.querySelector('.explore-tags-list');
                    if (tagsListScroll) {
                        tagsListScroll.scrollTop = previousScrollPosition;
                    }
                }, 100);
            }
            isFirstRender = false; // Mark first render as complete
        }
    }

    // Save session state to sessionStorage
    function saveSessionState() {
        const tagsListScroll = popupInstance?.element?.querySelector('.explore-tags-list');
        const scrollPosition = tagsListScroll?.scrollTop || 0;

        sessionStorage.setItem(sessionKey, JSON.stringify({
            viewMode: currentViewMode,
            searchQuery: searchQuery,
            scrollPosition: scrollPosition,
            selectedTagIds: selectedTags.map(t => t.id) // Save selected tag IDs for visual display
        }));
    }

    // Save scroll position periodically while scrolling
    function setupScrollSaving() {
        const tagsListScroll = popupInstance?.element?.querySelector('.explore-tags-list');
        if (tagsListScroll) {
            tagsListScroll.addEventListener('scroll', () => {
                saveSessionState();
            }, { passive: true });
        }
    }

    // Create tag element
    function createTagElement(tag) {
        const lang = getCurrentLanguage();
        const tagItem = document.createElement('div');
        tagItem.className = 'explore-tag-item';
        tagItem.dataset.tagId = tag.id;
        tagItem.dataset.packId = tag.packId;

        // Visual selection state check (calculated from the single source of truth: selectedTags)
        const isSelected = selectedTags.some(t => t.id === tag.id);
        if (isSelected) {
            tagItem.classList.add('selected');
        }

        const tagName = document.createElement('div');
        tagName.className = 'explore-tag-name';
        tagName.textContent = tag[`name_${lang}`] || tag.name_en;

        const tagCategory = document.createElement('div');
        tagCategory.className = 'explore-tag-category';
        tagCategory.textContent = `#${tag.packName.toLowerCase().replace(/\s+/g, '')}`;

        // Hide hashtag in category view since category headers already show it
        if (currentViewMode === 'category') {
            tagCategory.style.display = 'none';
        }

        tagItem.appendChild(tagName);
        tagItem.appendChild(tagCategory);

        // Click handler
        tagItem.addEventListener('click', async () => {
            const tagId = parseInt(tagItem.dataset.tagId);
            const tagObj = allTagsWithPacks.find(t => t.id === tagId);

            if (tagItem.classList.contains('selected')) {
                // If already selected, re-execute the action instead of deselecting
                // USER: "it should GO directly to the same tag again!!!!"
                await executeExploreAction();
                return;
            } else {
                // Deselect all other tags first (radio button behavior)
                const allTagItems = popupInstance?.element?.querySelectorAll('.explore-tag-item.selected');
                allTagItems?.forEach(item => {
                    item.classList.remove('selected');
                });
                selectedTags = [];

                // Select this one
                tagItem.classList.add('selected');
                selectedTags.push(tagObj);
                saveSessionState();

                // In single tag mode, immediately execute
                if (isSingleTagMode && selectedTags.length === 1) {
                    await executeExploreAction();
                    return; // Exit early since popup will close
                }
            }

            if (!isSingleTagMode) {
                updateActionButtonState();
            }
        });

        return tagItem;
    }

    // Execute explore action
    async function executeExploreAction() {
        if (!selectedArea || selectedTags.length === 0) return;

        try {
            // Group tags by pack for the query
            const tagsByPack = {};
            selectedTags.forEach(tag => {
                if (!tagsByPack[tag.packId]) {
                    tagsByPack[tag.packId] = [];
                }
                tagsByPack[tag.packId].push(tag);
            });

            // For simplicity, use the first pack (or we could query all packs)
            const firstPackId = Object.keys(tagsByPack)[0];
            const firstPack = allTagsWithPacks.find(t => t.packId === parseInt(firstPackId));
            const tagIds = selectedTags.map(t => t.id).filter(Boolean);

            // **NEW: Try to get cached minimal spots for entire area**
            const cachedAreaMinimal = getCachedMinimalSpotsForArea(selectedArea.id);

            let minimalSpots = [];
            if (cachedAreaMinimal && cachedAreaMinimal.minimal && cachedAreaMinimal.minimal.length > 0) {
                // Filter cached minimal data by selected tag IDs locally (no API call needed!)
                const filteredMinimal = cachedAreaMinimal.minimal.filter(spot => {
                    // spot.tags is an array of tag IDs this spot belongs to
                    if (!spot.tags || !Array.isArray(spot.tags)) return false;
                    // Check if spot has at least one of the selected tags
                    return tagIds.some(tagId => spot.tags.includes(tagId));
                });

                minimalSpots = convertMinimalToSpots(filteredMinimal);

                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[Explore] Using area cache: ${minimalSpots.length} spots for ${tagIds.length} tag(s) - NO API CALL!`);
                }
            }

            const lang = getCurrentLanguage();
            const tagLabels = selectedTags.map(t => t[`name_${lang}`] || t.name_en || t.slug);

            updateUrlForExplore(selectedArea, { slug: firstPack.packSlug, id: firstPackId }, selectedTags);

            // Immediately set cards context so cards UI knows the current tag selection
            try {
                // Provide pre-fetched tag list for this pack to avoid per-pack tag API calls
                const tagsForPack = allTagsWithPacks.filter(t => t.packId === parseInt(firstPackId)).map(t => ({
                    id: t.id,
                    slug: t.slug,
                    name_en: t.name_en,
                    name_es: t.name_es,
                    name_de: t.name_de,
                    icon: t.icon,
                    priority: t.priority,
                    spots_count: t.spots_count
                }));

                setCardsContext('explore', {
                    areaId: selectedArea.id,
                    packId: firstPackId,
                    tagIds,
                    tagLabels,
                    options: { preFetchedTagList: tagsForPack }
                }, true);
            } catch (e) {
                console.warn('[Explore] setCardsContext error (sync):', e);
            }

            // Immediately update the map with cached minimal spots (fast path)
            if (minimalSpots.length > 0) {
                await updateMapView({
                    spotsToDisplay: minimalSpots,
                    searchedTags: selectedTags.map(t => t.slug),
                    popupsToFade: [popupInstance]
                });

                // Kick off heavier work in background (non-blocking): set cards context, preload cards, fetch full spots
                (async () => {
                    try {
                        // Cards context already set synchronously; nothing to do here

                        // Preload all cards for all packs in the area (batch)
                        try {
                            triggerAreaBatch(selectedArea.id);
                        } catch (e) {
                            // ignore
                        }

                        // Preload cards for the displayed spots (background)
                        try {
                            const { preloadCardsForSpots } = await import('../utils/cache/index.js');
                            const spotIdsToPreload = minimalSpots.map(s => s.id).filter(Boolean);
                            if (spotIdsToPreload.length > 0) {
                                preloadCardsForSpots(spotIdsToPreload);
                            }
                        } catch (e) {
                            console.warn('[Explore] preloadCardsForSpots error:', e);
                        }

                        // Fetch full spot data in background and update map when ready
                        try {
                            const { getSpotsByIds } = await import('../utils/spot-queries.js');
                            const spotIds = minimalSpots.map(s => s.id).filter(Boolean);
                            if (spotIds.length > 0) {
                                const fullSpots = await getSpotsByIds(spotIds, { areaSlug: selectedArea.slug });
                                if (fullSpots && fullSpots.length > 0) {
                                    // Replace minimal markers with full spots (non-blocking UI update)
                                    await updateMapView({
                                        spotsToDisplay: fullSpots,
                                        searchedTags: selectedTags.map(t => t.slug),
                                        shouldAnimate: false, // NO ANIMATION for background upgrade to prevent flickering
                                        popupsToFade: []
                                    });
                                }
                            }
                        } catch (e) {
                            console.warn('[Explore] background full spots fetch error:', e);
                        }
                    } catch (err) {
                        console.warn('[Explore] Background tasks failed:', err);
                    }
                })();

            } else {
                // No cached data available - fallback to traditional API call (blocking)
                const spots = await getSpotsByExplore({
                    area: selectedArea.slug,
                    pack: firstPack.packSlug,
                    tags: selectedTags.map(t => t.slug),
                    areaId: selectedArea.id,
                    packId: firstPackId,
                    tagIds: tagIds.length > 0 ? tagIds : undefined
                });

                await updateMapView({
                    spotsToDisplay: spots,
                    searchedTags: selectedTags.map(t => t.slug),
                    popupsToFade: [popupInstance]
                });
            }

            popupInstance = null;
            if (window.currentPopupInstance === popupInstance) {
                window.currentPopupInstance = null;
            }

            trackExplore({
                area: { slug: selectedArea.slug, name: selectedArea.name_en },
                pack: { slug: firstPack.packSlug, name: firstPack.packName },
                tags: selectedTags.map(t => ({ slug: t.slug, name: t.name_en })),
                language: getCurrentLanguage()
            });

            window.dispatchEvent(new CustomEvent('exploreActionExecuted'));
        } catch (error) {
            if (LOGGING.ENABLED && LOGGING.MENU) console.error('Error executing explore action:', error);
        } finally {
            selectedTags = [];
        }
    }

    function updateActionButtonState() {
        if (!popupInstance || !popupInstance.element) return;
        const actionBtn = popupInstance.element.querySelector('.explore-search-action');
        if (actionBtn) {
            actionBtn.disabled = selectedTags.length === 0;
        }
    }
}

// Helper function to normalize icon IDs
function normalizeIconId(icon) {
    if (!icon) return null;
    const base = icon.toString().split('/').pop();
    return base.replace(/\.svg$/i, '');
}
